import { getLocaleString } from 'typescript/utilities/locales';
import { Locale } from 'typescript/constants/localization';
import t from 'typescript/utilities/translate';

/**
 * creates a Intl Number Formatter for the given locale and currency
 * @param locale - locale string in the format of languageCode-CountryCode
 * @param currency - currency code
 * @returns Intl.NumberFormat - number formatter
 */
export const getNumberFormatter = ({
  locale = getLocaleString(),
  currency,
}: {
  locale?: Locale;
  currency: string;
}): Intl.NumberFormat => {
  // Some older versions of Safari don't support currencyDisplay: narrowSymbol
  // so we need a fallback here
  try {
    return Intl.NumberFormat(locale, {
      currency,
      style: 'currency',
      ...(currency === 'CAD' && { currencyDisplay: 'narrowSymbol' }),
    });
  } catch {
    return Intl.NumberFormat(locale, {
      currency,
      style: 'currency',
      currencyDisplay: 'symbol',
    });
  }
};

/**
 * Formats a number to a currency string
 * @param amount - amount to be formatted
 * @param currency - currency code
 * @returns string - formatted currency string
 */
export const toCurrency = ({
  amount,
  currency,
}: {
  amount: number;
  currency: string;
}): string => {
  return getNumberFormatter({ currency }).format(amount);
};

/**
 * Formats a number to a currency string
 * if the amount is 0, return the Free translation string
 * @param amount - amount to be formatted
 * @param currency - currency code
 * @returns string - formatted currency string or Free translation string
 */
export const getCurrencyString = ({
  amount,
  currency,
}: {
  amount: number;
  currency: string;
}): string => {
  // if amount is 0, return the Free translation
  return amount
    ? toCurrency({ amount, currency })
    : t('checkout.shipping.cost.free');
};
